/**
 * Price series utilities.
 * Pure functions for computing realtime dot positions and series type checks.
 * No React dependencies.
 */

import { CANDLESTICK_COLORS } from "./colorPalette";
import { getPointValue } from "./dataUtils";
import type { ChartLayout, OhlcvPoint, PricePoint, Scale } from "~types/types";

export interface RealtimeDotPosition {
  /** X position of the dot */
  x: number;
  /** Y position of the dot */
  y: number;
}

export interface CalculateRealtimeDotOptions {
  /** Visible data points */
  visibleData: (PricePoint | OhlcvPoint)[];
  /** X and Y scales */
  scales: { x: Scale; y: Scale };
  /** Chart layout for bounds checking */
  layout: ChartLayout;
}

/**
 * Calculate the position of the realtime dot at the last data point.
 * Returns null if no data or point is outside visible area.
 */
export function calculateRealtimeDotPosition(
  options: CalculateRealtimeDotOptions
): RealtimeDotPosition | null {
  const { visibleData, scales, layout } = options;

  if (visibleData.length === 0) {
    return null;
  }

  const lastPoint = visibleData.at(-1);

  if (!lastPoint) {
    return null;
  }

  const lastValue = getPointValue(lastPoint);
  const x = scales.x.scale(lastPoint.timestamp);
  const y = scales.y.scale(lastValue);

  // Don't render if point is outside visible area
  if (
    x < layout.priceArea.x ||
    x > layout.priceArea.x + layout.priceArea.width
  ) {
    return null;
  }

  return { x, y };
}

/**
 * Gradient colors for line chart based on price direction.
 * Uses lighter versions of candlestick colors for gradient fill.
 */
export const GRADIENT_COLORS = {
  positive: {
    line: CANDLESTICK_COLORS.up,
  },
  negative: {
    line: CANDLESTICK_COLORS.down,
  },
  neutral: {
    line: CANDLESTICK_COLORS.unchanged,
  },
} as const;

/**
 * Check if a point should be rendered as a line chart point.
 * Returns true for line charts (PricePoint or OhlcvPoint used as line).
 */
export function isLineChartData(
  data: (PricePoint | OhlcvPoint)[]
): data is PricePoint[] {
  if (data.length === 0) return true;

  const first = data[0];

  // Check if it's a simple PricePoint (has value property)
  return first !== undefined && "value" in first;
}
